/**
 * Supabase Browser Client
 *
 * Creates a Supabase client for use in browser/client-side code.
 * This is the canonical location for client-side Supabase initialization.
 */

import { createBrowserClient as createSupabaseBrowserClient } from "@supabase/ssr"
import { getSupabaseConfig } from "./config"

// Cached client instance to avoid creating multiple clients
let cachedClient: ReturnType<typeof createSupabaseBrowserClient> | null = null
let isConfigured = false

/**
 * Check if Supabase is properly configured
 */
export function isSupabaseConfigured(): boolean {
  const config = getSupabaseConfig()
  return config.isConfigured !== false
}

/**
 * Create a Supabase client for browser-side usage
 * Returns null if Supabase is not configured
 */
export function createClient() {
  const config = getSupabaseConfig()

  // If not configured, return a mock client that won't throw
  if (config.isConfigured === false) {
    if (typeof window !== "undefined") {
      console.warn("[Supabase Client] Supabase is not configured. Authentication features will not work.")
    }
    // Return cached client if we already created one
    if (cachedClient) return cachedClient
    
    // Create a client with placeholder values - it will fail on actual API calls
    // but at least won't throw immediately
    try {
      cachedClient = createSupabaseBrowserClient(config.url, config.anonKey)
      isConfigured = false
      return cachedClient
    } catch {
      // If even placeholder creation fails, throw a clearer error
      throw new Error(
        "Supabase is not configured. Please set NEXT_PUBLIC_SUPABASE_URL and NEXT_PUBLIC_SUPABASE_ANON_KEY environment variables.",
      )
    }
  }

  if (typeof window !== "undefined" && config.environment !== "production") {
    console.log("[v0][Supabase] Client configuration:", {
      environment: config.environment,
      supabaseUrl: config.url,
      redirectUrl: config.redirectUrl,
      isPreviewOrStaging: config.isPreviewOrStaging,
    })
  }

  if (cachedClient && isConfigured) return cachedClient

  cachedClient = createSupabaseBrowserClient(config.url, config.anonKey)
  isConfigured = true
  return cachedClient
}

/**
 * Alias for createClient - used by components that import as createBrowserClient
 * This provides backwards compatibility with the @supabase/ssr naming convention
 */
export function createBrowserClient() {
  return createClient()
}
